import { useEffect, useState, useMemo, useCallback } from "react";

interface Particle {
  id: number;
  x: number;
  y: number;
  size: number;
  baseOpacity: number;
  duration: number;
  delay: number;
  layer: 'far' | 'mid' | 'near';
  directionX: number;
  directionY: number;
}

const particleColor = '0 0% 100%'; // White/neutral

const MouseFollowBackground = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [smoothPosition, setSmoothPosition] = useState({ x: 0, y: 0 });
  const [windowSize, setWindowSize] = useState({ width: 0, height: 0 });
  const [isDark, setIsDark] = useState(false);

  // Check for dark mode
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDark(document.documentElement.classList.contains('dark'));
    };
    
    checkDarkMode();
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    
    return () => observer.disconnect();
  }, []);

  // Generate layered particles with monochrome colors
  const particles = useMemo<Particle[]>(() => {
    // Far particles - tiny, slow, dim
    const farParticles = Array.from({ length: 42 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 1.5 + 0.5,
      baseOpacity: Math.random() * 0.3 + 0.1,
      duration: Math.random() * 30 + 20,
      delay: Math.random() * -30,
      layer: 'far' as const,
      directionX: (Math.random() - 0.5) * 30,
      directionY: (Math.random() - 0.5) * 30,
    }));

    // Mid particles - medium size and speed
    const midParticles = Array.from({ length: 27 }, (_, i) => ({
      id: i + 42,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 2 + 1.5,
      baseOpacity: Math.random() * 0.4 + 0.2,
      duration: Math.random() * 18 + 12,
      delay: Math.random() * -18,
      layer: 'mid' as const,
      directionX: (Math.random() - 0.5) * 50,
      directionY: (Math.random() - 0.5) * 50,
    }));

    // Near particles - larger, brighter, more glow
    const nearParticles = Array.from({ length: 11 }, (_, i) => ({
      id: i + 69,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 3 + 2,
      baseOpacity: Math.random() * 0.5 + 0.4,
      duration: Math.random() * 10 + 8,
      delay: Math.random() * -10,
      layer: 'near' as const,
      directionX: (Math.random() - 0.5) * 80,
      directionY: (Math.random() - 0.5) * 80,
    }));

    return [...farParticles, ...midParticles, ...nearParticles];
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    const handleResize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    };

    handleResize();
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Smooth mouse follow
  useEffect(() => {
    const animate = () => {
      setSmoothPosition(prev => ({
        x: prev.x + (mousePosition.x - prev.x) * 0.06,
        y: prev.y + (mousePosition.y - prev.y) * 0.06,
      }));
    };
    const interval = setInterval(animate, 16);
    return () => clearInterval(interval);
  }, [mousePosition]);

  // Particle style with subtle glow - using transform: scale() to avoid CLS
  const getParticleStyle = useCallback((particle: Particle) => {
    if (!isDark) return { display: 'none' };

    const particleX = (particle.x / 100) * windowSize.width;
    const particleY = (particle.y / 100) * windowSize.height;
    
    const distance = Math.sqrt(
      Math.pow(mousePosition.x - particleX, 2) + 
      Math.pow(mousePosition.y - particleY, 2)
    );
    
    const proximityRadius = particle.layer === 'near' ? 300 : particle.layer === 'mid' ? 200 : 150;
    const proximityFactor = Math.max(0, 1 - distance / proximityRadius);
    const boostedOpacity = particle.baseOpacity + proximityFactor * 0.4;
    // Calculate scale factor instead of changing width/height to avoid CLS
    const maxBoost = particle.layer === 'near' ? 4 : 2;
    const scaleFactor = 1 + (proximityFactor * maxBoost) / particle.size;
    const glowIntensity = 1 + proximityFactor * 2;

    const glowSize = particle.layer === 'near' ? particle.size * 8 : particle.layer === 'mid' ? particle.size * 5 : particle.size * 3;

    return {
      left: `${particle.x}%`,
      top: `${particle.y}%`,
      width: particle.size,
      height: particle.size,
      opacity: Math.min(boostedOpacity, 1),
      background: `hsl(${particleColor})`,
      boxShadow: `
        0 0 ${glowSize * glowIntensity}px hsl(${particleColor} / ${0.5 * glowIntensity}),
        0 0 ${glowSize * 2 * glowIntensity}px hsl(${particleColor} / ${0.25 * glowIntensity}),
        0 0 ${glowSize * 3}px hsl(${particleColor} / 0.1)
      `,
      animation: `float-space-${particle.id % 4} ${particle.duration}s ease-in-out infinite`,
      animationDelay: `${particle.delay}s`,
      transform: `scale(${scaleFactor})`,
      transition: "transform 0.4s ease-out, opacity 0.4s ease-out, box-shadow 0.4s ease-out",
      willChange: "transform, opacity",
      '--dx': `${particle.directionX}px`,
      '--dy': `${particle.directionY}px`,
    } as React.CSSProperties;
  }, [mousePosition, windowSize, isDark]);

  return (
    <div className="fixed inset-0 -z-10 overflow-hidden" style={{ contain: 'layout style paint' }}>
      {/* Light mode - simple clean background */}
      {!isDark && (
        <div className="absolute inset-0 bg-background" />
      )}

      {/* Dark mode - deep space background */}
      {isDark && (
        <>
          {/* Base gradient - deep blues and purples */}
          <div 
            className="absolute inset-0"
            style={{
              background: `
                radial-gradient(ellipse 150% 100% at 50% 0%, hsl(240 30% 8%) 0%, hsl(260 40% 4%) 40%, hsl(240 50% 2%) 100%)
              `,
            }}
          />
          
          {/* Nebula accent layers */}
          <div 
            className="absolute inset-0"
            style={{
              background: `
                radial-gradient(ellipse 60% 50% at 10% 90%, hsl(270 60% 20% / 0.15) 0%, transparent 50%),
                radial-gradient(ellipse 50% 40% at 90% 10%, hsl(200 80% 30% / 0.1) 0%, transparent 50%),
                radial-gradient(ellipse 40% 30% at 70% 80%, hsl(330 70% 30% / 0.08) 0%, transparent 40%)
              `,
            }}
          />

          {/* Depth vignette */}
          <div 
            className="absolute inset-0"
            style={{
              background: `radial-gradient(ellipse 100% 100% at 50% 50%, transparent 20%, hsl(240 50% 1% / 0.9) 100%)`,
            }}
          />
        </>
      )}

      {/* Floating particles - dark mode only - always rendered to prevent CLS */}
      <div className="absolute inset-0" style={{ contain: 'layout style paint' }}>
        {particles.map((particle) => (
          <div
            key={particle.id}
            className="absolute rounded-full"
            style={getParticleStyle(particle)}
          />
        ))}
      </div>


      {/* Inline keyframes for unique particle movements */}
      <style>{`
        @keyframes float-space-0 {
          0%, 100% { transform: translate(0, 0); }
          25% { transform: translate(var(--dx, 20px), calc(var(--dy, 20px) * 0.5)); }
          50% { transform: translate(calc(var(--dx, 20px) * 0.3), var(--dy, 20px)); }
          75% { transform: translate(calc(var(--dx, 20px) * 0.7), calc(var(--dy, 20px) * 0.3)); }
        }
        @keyframes float-space-1 {
          0%, 100% { transform: translate(0, 0); }
          33% { transform: translate(var(--dx, 15px), calc(var(--dy, 15px) * 0.6)); }
          66% { transform: translate(calc(var(--dx, 15px) * 0.4), var(--dy, 15px)); }
        }
        @keyframes float-space-2 {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(var(--dx, 25px), var(--dy, 25px)); }
        }
        @keyframes float-space-3 {
          0%, 100% { transform: translate(0, 0); }
          20% { transform: translate(calc(var(--dx, 10px) * 0.5), var(--dy, 10px)); }
          40% { transform: translate(var(--dx, 10px), calc(var(--dy, 10px) * 0.7)); }
          60% { transform: translate(calc(var(--dx, 10px) * 0.8), calc(var(--dy, 10px) * 0.3)); }
          80% { transform: translate(calc(var(--dx, 10px) * 0.2), calc(var(--dy, 10px) * 0.8)); }
        }
      `}</style>
    </div>
  );
};

export default MouseFollowBackground;